import { useSelectedAgentModel } from "@ext/agent/components/hooks/useSelectedAgentModel";
import { ChatReasoningEffortSlider } from "@ext/agent/components/panel/ChatReasoningEffortSlider";
import { useAgentChatIsOpen } from "@ext/agent/components/store/AgentChatIsOpenStore";
import { formatReasoningEffort } from "@ext/agent/components/utils/formatReasoningEffort";
import type { AiAgentModel } from "@ext/agent/llm";
import t, { type TranslationKey } from "@ext/localization/locale/translate";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTriggerButton,
} from "@ui-kit/Dropdown";
import { Icon, type IconCode } from "@ui-kit/Icon";
import { TextOverflowTooltip, Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useState } from "react";

type Props = {
	models: AiAgentModel[];
	onModelChange: (model: AiAgentModel) => void;
	disabled?: boolean;
};

const getModelLabel = (model: AiAgentModel): string => model.title || model.name;

const AI_MODEL_BADGES = {
	expensive: {
		icon: "circle-dollar-sign",
		tooltip: "agent.badges.expensive",
	},
	best_choice: {
		icon: "circle-check",
		tooltip: "agent.badges.best-choice",
	},
} satisfies Record<string, { icon: IconCode; tooltip: TranslationKey }>;

const ChatModelBadges = ({ badges }: { badges?: string[] }) => {
	const badgesList = (badges ?? [])
		.filter((badge): badge is keyof typeof AI_MODEL_BADGES => Boolean(badge))
		.map((badge) => AI_MODEL_BADGES[badge as keyof typeof AI_MODEL_BADGES]);

	if (!badgesList.length) return null;

	return (
		<span className="flex shrink-0 items-center gap-1">
			{badgesList.map((badge) => (
				<Tooltip key={badge.tooltip}>
					<TooltipTrigger asChild>
						<span className="inline-flex text-muted">
							<Icon className="size-3" icon={badge.icon} />
						</span>
					</TooltipTrigger>
					<TooltipContent>{t(badge.tooltip)}</TooltipContent>
				</Tooltip>
			))}
		</span>
	);
};

const ChatModelMenu = ({ models, onModelChange, disabled }: Props) => {
	const selection = useSelectedAgentModel();
	const isPanelOpen = useAgentChatIsOpen();
	const [open, setOpen] = useState(false);
	if (open && !isPanelOpen) setOpen(false);

	if (!selection) return null;
	const { model: selectedModel, reasoningEffort } = selection;

	const handleValueChange = (id: string) => {
		const model = models.find((m) => m.id === id);
		if (model) onModelChange(model);
	};

	return (
		<DropdownMenu modal={false} onOpenChange={setOpen} open={open}>
			<DropdownMenuTriggerButton
				className="h-7 w-full min-w-0 px-1.5 font-normal"
				containerClassName="gap-1"
				disabled={disabled}
				endIcon="chevron-down"
				iconClassName="text-muted"
				size="xs"
				variant="ghost"
			>
				<TextOverflowTooltip className="min-w-0 truncate">{getModelLabel(selectedModel)}</TextOverflowTooltip>
				{reasoningEffort && (
					<span className="shrink-0 font-normal text-muted">{formatReasoningEffort(reasoningEffort)}</span>
				)}
			</DropdownMenuTriggerButton>
			<DropdownMenuContent align="start" side="top">
				<DropdownMenuRadioGroup
					indicatorIconPosition="start"
					onValueChange={handleValueChange}
					value={selectedModel.id}
				>
					{models.map((model) => (
						<DropdownMenuRadioItem key={model.id} value={model.id}>
							<span className="flex min-w-0 items-center gap-1 justify-between">
								<TextOverflowTooltip className="truncate whitespace-nowrap">
									{getModelLabel(model)}
								</TextOverflowTooltip>
								<ChatModelBadges badges={model.badges} />
							</span>
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
				<ChatReasoningEffortSlider />
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export { ChatModelMenu };
